import { Box, Drawer, IconButton, Typography } from "@mui/material";
import { useState } from "react";
import CalendarDaysIcon from "@/modules/common/components/ui-kit/icons/calendar-days-icon";
import ClipboardListIcon from "@/modules/common/components/ui-kit/icons/clipboard-list-icon";
import Meetings from "@/modules/meetings/components/meetings/meetings-model";
import Tasks from "@/modules/meetings/components/tasks/tasks-model";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import CloseIcon from "@mui/icons-material/Close";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { Link } from "react-router-dom";
import { PathConstants } from "@/routes/constants";

const DesktopSideBar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState<
    "meetings" | "tasks" | null
  >(null);
  const screen = useScreenSize();

  const handleCalendarClick = () => {
    setDrawerContent("meetings");
    setDrawerOpen(true);
  };

  const handleClipboardClick = () => {
    setDrawerContent("tasks");
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setDrawerContent(null);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          position: "fixed",
          right: 0,
          top: 0,
          height: "100%",
          width: "60px",
          backgroundColor: nawyTheme.nawy.colors.semantic.layer.accentDark,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            marginTop: "120px",
            width: "100%",
            gap: "20px",
            alignItems: "center",
            paddingX: "20px",
          }}
        >
          <Box
            component="button"
            sx={{
              cursor: "pointer",
              padding: "12px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background-color 0.2s ease",
              pointerEvents: "auto",
              backgroundColor: "transparent",
              border: "none",
              outline: "none",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
              "&:active": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              },
            }}
            onClick={handleCalendarClick}
          >
            <CalendarDaysIcon width={20} height={20} />
          </Box>
          <Box
            component="button"
            sx={{
              cursor: "pointer",
              padding: "12px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background-color 0.2s ease",
              pointerEvents: "auto",
              backgroundColor: "transparent",
              border: "none",
              outline: "none",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
              "&:active": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              },
            }}
            onClick={handleClipboardClick}
          >
            <ClipboardListIcon width={20} height={20} />
          </Box>
        </Box>
      </Box>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        slotProps={{
          paper: {
            sx: {
              width: 400,
            },
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: nawyTheme.nawy.colors.semantic.layer.primary,
            display: "flex",
            flexDirection: "column",
            position: "relative",
            border: `1px solid ${nawyTheme.nawy.colors.semantic.layer.secondary}`,
            width: "100%",
            height: "100vh",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              borderBottom: `1px solid ${nawyTheme.nawy.colors.semantic.layer.secondary}`,
              alignItems: "center",
              padding: `${nawyTheme.nawy.spacing.lg} ${nawyTheme.nawy.spacing.xl} ${nawyTheme.nawy.spacing.md} ${nawyTheme.nawy.spacing.xl}`,
              width: "90%",
              flexShrink: 0,
            }}
          >
            {!screen.isMobile && (
              <Typography
                variant="h6"
                sx={{
                  ...nawyTheme.nawy.typography.english.variants.headline.xs,
                  color: nawyTheme.nawy.colors.semantic.text.primary,
                }}
              >
                {drawerContent === "meetings" ? "Meetings" : "Tasks"}
              </Typography>
            )}
            {drawerContent === "tasks" && (
              <Link
                to={PathConstants.TASK_DASHBOARD}
                style={{ textDecoration: "none" }}
                onClick={handleCloseDrawer}
              >
                <Typography
                  sx={{
                    color: nawyTheme.nawy.colors.semantic.text.accent,
                    ...nawyTheme.nawy.typography.english.variants.body.md
                      .semibold,
                    fontFamily: nawyTheme.nawy.typography.english.fontFamily,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      cursor: "pointer",
                    },
                  }}
                >
                  All Tasks
                </Typography>
              </Link>
            )}

            <IconButton
              onClick={handleCloseDrawer}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 10,
                color: nawyTheme.nawy.colors.primitives.grey[700],
                "&:hover": {
                  backgroundColor: nawyTheme.nawy.colors.primitives.grey[100],
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: "auto",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {drawerContent === "meetings" && <Meetings />}
            {drawerContent === "tasks" && <Tasks />}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default DesktopSideBar;
